/**
 * API client public Vitrine TEXTIPRINT (Sprint 3).
 * Endpoints anonymes /v1/public/textprint/boutique/* — pas de JWT.
 * Utilise fetch direct pour éviter l'interceptor auth d'axios.
 */

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1';

async function fetchPublic<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
  const url = new URL(`${API_BASE}/public/textprint/boutique${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== '') url.searchParams.set(k, String(v));
    });
  }
  const res = await fetch(url.toString(), {
    headers: { 'Accept': 'application/json' },
  });
  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${await res.text()}`);
  }
  const json = await res.json();
  // Le TransformInterceptor wrappe { success, data }
  return (json.data ?? json) as T;
}

export interface VitrineFamille {
  id:     string;
  code:   string;
  nom:    string;
  ordre:  number;
  iconUrl: string | null;
}

export interface VitrineProduit {
  id:                    string;
  sku:                   string;
  nom:                   string;
  description:           string | null;
  marque:                string | null;
  refConstructeur:       string | null;
  compatibilites:        string[];
  photos:                string[];
  prixUnitaireHtCents:   string;
  tvaPct:                string;
  unite:                 string;
  poidsKg:               string | null;
  personnalisable:       boolean;
  optionsPerso:          Record<string, unknown> | null;
  quantiteMinimum:       number | null;
  delaiProductionJours:  number | null;
  prixSetupCents:        string | null;
  featured:              boolean;
  famille?:              { id: string; code: string; nom: string };
  sousCategorie?:        { id: string; code: string; nom: string } | null;
  variantes?:            Array<{ id: string; taille: string | null; couleur: string | null; prixVarCents: string | null }>;
  remisesVolume?:        Array<{ id: string; quantiteMin: number; remisePct: string }>;
}

export interface VitrineProduitsPage {
  data: VitrineProduit[];
  meta: { page: number; limit: number; total: number; pages: number };
}

export const vitrineApi = {
  familles: () => fetchPublic<VitrineFamille[]>('/familles'),

  produits: (params: {
    page?:           number;
    limit?:          number;
    famille?:        string;
    featured?:       boolean;
    personnalisable?: boolean;
    search?:         string;
  } = {}) =>
    fetchPublic<VitrineProduitsPage>('/produits', {
      page:           params.page?.toString(),
      limit:          params.limit?.toString(),
      famille:        params.famille,
      featured:       params.featured ? 'true' : undefined,
      personnalisable: params.personnalisable ? 'true' : undefined,
      search:         params.search,
    }),

  produit: (sku: string) => fetchPublic<VitrineProduit>(`/produits/${encodeURIComponent(sku)}`),
};
